import {
  isUsBvShopifyPlansPayloadOk,
  validateUsZipAndPricingWithMeta,
  type UsBvShopifyPlansResponse,
} from 'utils/bloodVisionUpsell';
import {
  getBloodVisionUpsellCards,
  type BvUpsellCard,
} from 'content/products/bloodVisionUpsell';
import { getCookie } from 'utils/cookie';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';

export type BvUsZipPlansSnapshot = {
  committedZip: string | null;
  shopifyPlansResponse: UsBvShopifyPlansResponse | null;
  revision: number;
  commit: (zip: string, response: UsBvShopifyPlansResponse) => void;
  clear: () => void;
  getReuseForZip: (zip: string) => {
    zip: string;
    response: UsBvShopifyPlansResponse;
  } | null;
};

const listeners = new Set<() => void>();
const BV_US_ZIP_SESSION_KEY = 'bv_us_zip_plans_session_v1';
const BV_US_ZIP_COOKIE = 'bv_pincode_us';
const warmupRequestsByZip = new Map<string, Promise<boolean>>();

let committedZip: string | null = null;
let shopifyPlansResponse: UsBvShopifyPlansResponse | null = null;
let revision = 0;

let snapshotCache: BvUsZipPlansSnapshot | null = null;
let snapshotCacheAtRevision = -1;

function hydrateFromSessionStorage() {
  if (typeof window === 'undefined') return;
  const raw = window.sessionStorage.getItem(BV_US_ZIP_SESSION_KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as {
      zip?: unknown;
      response?: unknown;
    };
    const zip = typeof parsed.zip === 'string' ? parsed.zip.trim() : '';
    const response = parsed.response as UsBvShopifyPlansResponse | undefined;
    if (/^\d{5}$/.test(zip) && response && isUsBvShopifyPlansPayloadOk(response)) {
      committedZip = zip;
      shopifyPlansResponse = response;
      revision = 1;
      return;
    }
  } catch {
    // ignore malformed session state
  }
  window.sessionStorage.removeItem(BV_US_ZIP_SESSION_KEY);
}

function persistToSessionStorage() {
  if (typeof window === 'undefined') return;
  if (committedZip && shopifyPlansResponse) {
    window.sessionStorage.setItem(
      BV_US_ZIP_SESSION_KEY,
      JSON.stringify({
        zip: committedZip,
        response: shopifyPlansResponse,
      }),
    );
    return;
  }
  window.sessionStorage.removeItem(BV_US_ZIP_SESSION_KEY);
}

function emit() {
  listeners.forEach((listener) => listener());
}

function commit(zip: string, response: UsBvShopifyPlansResponse) {
  committedZip = zip.trim();
  shopifyPlansResponse = response;
  revision += 1;
  snapshotCache = null;
  snapshotCacheAtRevision = -1;
  persistToSessionStorage();
  emit();
}

function clear() {
  committedZip = null;
  shopifyPlansResponse = null;
  revision += 1;
  snapshotCache = null;
  snapshotCacheAtRevision = -1;
  persistToSessionStorage();
  emit();
}

function getReuseForZip(zip: string) {
  const normalized = zip.trim();
  if (
    !committedZip ||
    !shopifyPlansResponse ||
    committedZip !== normalized
  ) {
    return null;
  }
  return { zip: committedZip, response: shopifyPlansResponse };
}

function buildSnapshot(): BvUsZipPlansSnapshot {
  return {
    committedZip,
    shopifyPlansResponse,
    revision,
    commit,
    clear,
    getReuseForZip,
  };
}

const readUsZipFromCookie = (): string => {
  if (typeof document === 'undefined') return '';
  const raw = getCookie(BV_US_ZIP_COOKIE);
  if (typeof raw !== 'string') return '';
  try {
    return decodeURIComponent(raw).trim().replace(/\D/g, '');
  } catch {
    return raw.trim().replace(/\D/g, '');
  }
};

const getWarmupCard = (preferredCard?: BvUpsellCard): BvUpsellCard | null => {
  if (preferredCard?.pincodeBehavior === 'us_price_and_availability') {
    return preferredCard;
  }
  const cards = getBloodVisionUpsellCards(ShopifyStore.US);
  return (
    cards.find((card) => card.pincodeBehavior === 'us_price_and_availability') ??
    null
  );
};

export async function bvUsZipPlansEnsureWarmFromCookie(
  preferredCard?: BvUpsellCard,
): Promise<boolean> {
  const cookieZip = readUsZipFromCookie();
  if (!/^\d{5}$/.test(cookieZip)) return false;
  if (getReuseForZip(cookieZip)) return true;
  const existing = warmupRequestsByZip.get(cookieZip);
  if (existing) {
    return existing;
  }
  const warmupCard = getWarmupCard(preferredCard);
  if (!warmupCard) return false;
  const request = (async () => {
    const meta = await validateUsZipAndPricingWithMeta(warmupCard, cookieZip);
    if (
      meta.shopifyPlansResponse &&
      isUsBvShopifyPlansPayloadOk(meta.shopifyPlansResponse)
    ) {
      commit(meta.normalizedZip, meta.shopifyPlansResponse);
      return true;
    }
    return false;
  })();
  warmupRequestsByZip.set(cookieZip, request);
  try {
    return await request;
  } finally {
    warmupRequestsByZip.delete(cookieZip);
  }
}

hydrateFromSessionStorage();

export function bvUsZipPlansSubscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function bvUsZipPlansGetSnapshot(): BvUsZipPlansSnapshot {
  if (snapshotCache && snapshotCacheAtRevision === revision) {
    return snapshotCache;
  }
  snapshotCache = buildSnapshot();
  snapshotCacheAtRevision = revision;
  return snapshotCache;
}

function noop(): void {
  return;
}

const serverSnapshot: BvUsZipPlansSnapshot = Object.freeze({
  committedZip: null,
  shopifyPlansResponse: null,
  revision: 0,
  commit: noop,
  clear: noop,
  getReuseForZip: () => null,
});

export function bvUsZipPlansGetServerSnapshot(): BvUsZipPlansSnapshot {
  return serverSnapshot;
}
